"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseProject = parseProject;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const orcadValidation_1 = require("./orcadValidation");
const mergeDisjointComponentsUtil_1 = require("../mergeDisjointComponentsUtil");
const OrcadDsnOrchestratedTransformer_1 = require("./transformers/orcadDsnOrchestratedTransformer/OrcadDsnOrchestratedTransformer");
const OrcadDsnUtils_1 = require("./transformers/orcadDsnOrchestratedTransformer/Generators/OrcadDsnUtils");
const orcadDsnTransformer_1 = require("./transformers/orcadDsnTransformer");
const orcadLayers_1 = require("./orcadLayers");
const orcadLogicalSchematicReferenceResolver_1 = require("./orcadLogicalSchematicReferenceResolver");
const ModelText_1 = require("../../ModelText");
const orcadIntersheetReferences_1 = require("./orcadIntersheetReferences");
const OrcadDsnSymbolUtils_1 = require("./transformers/orcadDsnOrchestratedTransformer/Generators/OrcadDsnSymbolUtils");
const OrCadIntersheetReferenceResultType = 'orcad-intersheet-reference';
const OrCadStoredIntersheetReferenceResultType = 'iref-stored-uncorroborated';
const OrCadIntersheetReferenceMismatchResultType = 'iref-computed-mismatch';
const IntersheetReferencePropertyName = 'IREF';
const OnlPlacementOmittedPropertyKeys = new Set(['TIMESTAMP', 'SOURCEPACKAGE', 'VALUE']);
const OnlPlacementOnlyPropertyKeys = new Set(['BOMIGNORE', 'MODEL3D', 'OFFSET3D', 'PACKTYPE', 'SPLITINST']);
function onlPropertyKey(name) {
    return name.replace(/[\s_-]/g, '').toUpperCase();
}
function getDefaultSettings() {
    return {
        mergeDisjointComponents: false,
        legacyParser: false,
        combobulationDebug: false,
        transformOpts: {},
    };
}
function hasReferencedProjectFile(def, relativeToFile, referencedFile, referencedViewName) {
    return ((0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(collectLogicalSchematicEntries(def), relativeToFile, referencedFile, referencedViewName).status === 'resolved');
}
function auditReferencedProjectFiles(def) {
    const entries = collectLogicalSchematicEntries(def);
    const unresolvedReferences = new Map();
    const viewResolvedReferences = new Map();
    for (const [, xmlSch] of Object.entries(def.schematics)) {
        if (isValidOrCADLogicalSchematic(xmlSch)) {
            for (const referencedFile of xmlSch.validationResult.data.referencedFiles ?? []) {
                if (hasReferencedProjectFile(def, xmlSch.originalFileName, referencedFile)) {
                    continue;
                }
                const viewNames = referencedViewNames(xmlSch.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema], referencedFile);
                const filenameResolution = (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(entries, xmlSch.originalFileName, referencedFile);
                if (filenameResolution.status === 'resolved') {
                    continue;
                }
                const viewResolutions = viewNames.map((viewName) => ({
                    viewName,
                    resolution: (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(entries, xmlSch.originalFileName, referencedFile, viewName),
                }));
                const resolvedViews = viewResolutions.filter((row) => row.resolution.status === 'resolved' && row.resolution.matchKind === 'view');
                const resolvedFiles = new Set(resolvedViews.map((row) => row.resolution.candidate.sourceFile));
                if (resolvedViews.length > 0 && resolvedViews.length === viewResolutions.length && resolvedFiles.size === 1) {
                    for (const { resolution, viewName } of resolvedViews) {
                        const key = `${(0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(xmlSch.originalFileName)}\0${resolution.normalizedPath}\0${viewName.toLowerCase()}`;
                        viewResolvedReferences.set(key, {
                            referencedFrom: xmlSch.originalFileName,
                            referencedFile,
                            normalizedPath: resolution.normalizedPath,
                            resolvedFile: resolution.candidate.sourceFile,
                            viewName,
                            matchKind: 'view',
                        });
                    }
                    continue;
                }
                const ambiguousResolution = viewResolutions.find((row) => row.resolution.status === 'unresolved' && row.resolution.reason === 'ambiguous')?.resolution;
                const resolution = ambiguousResolution ?? filenameResolution;
                if (resolution.status === 'resolved') {
                    continue;
                }
                const key = `${(0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(xmlSch.originalFileName)}\0${resolution.normalizedPath}`;
                unresolvedReferences.set(key, {
                    referencedFrom: xmlSch.originalFileName,
                    referencedFile,
                    normalizedPath: resolution.normalizedPath,
                    reason: resolution.reason,
                    candidates: resolution.candidates,
                });
            }
        }
    }
    return {
        hasValidSchematics: entries.length > 0,
        unresolvedReferences: Array.from(unresolvedReferences.values()),
        viewResolvedReferences: Array.from(viewResolvedReferences.values()),
    };
}
function referencedViewNames(design, referencedFile) {
    const normalizedReference = referencedFile.replace(/\\/g, '/').toLowerCase();
    const names = new Set();
    for (const schematic of design.Schematic) {
        for (const page of schematic.Page) {
            for (const instance of [...(page.DrawnInst ?? []), ...(page.PartInst ?? [])]) {
                if (instance.ContentsLibName.replace(/\\/g, '/').toLowerCase() === normalizedReference &&
                    instance.ContentsViewName) {
                    names.add(instance.ContentsViewName);
                }
            }
        }
    }
    return [...names].sort((left, right) => left.localeCompare(right));
}
function isValidOrCADLogicalSchematic(schematic) {
    return Boolean(schematic.validationResult?.isValid &&
        schematic.validationResult.data.fileFormat.cadTool === 'orcad' &&
        (schematic.validationResult.data.fileFormat.type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematicXml ||
            schematic.validationResult.data.fileFormat.type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic) &&
        schematic.validationResult.data.metadata?.[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema]);
}
function logicalSchematicDedupeKey(fileName) {
    return (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(fileName).replace(/\.(dsn|xml)$/i, '');
}
function logicalSchematicPreference(schematic) {
    const type = schematic.validationResult?.data?.fileFormat.type;
    if (type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic) {
        return 2;
    }
    if (type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematicXml) {
        return 1;
    }
    return 0;
}
function preferDsnLogicalSchemas(def) {
    const selectedByLogicalPath = new Map();
    for (const [key, schematic] of Object.entries(def.schematics)) {
        if (!isValidOrCADLogicalSchematic(schematic)) {
            continue;
        }
        const logicalKey = logicalSchematicDedupeKey(schematic.originalFileName);
        const preference = logicalSchematicPreference(schematic);
        const selected = selectedByLogicalPath.get(logicalKey);
        if (!selected || preference > selected.preference) {
            selectedByLogicalPath.set(logicalKey, { key, preference });
        }
    }
    const selectedKeys = new Set(Array.from(selectedByLogicalPath.values()).map((selected) => selected.key));
    const schematics = Object.fromEntries(Object.entries(def.schematics).filter(([key, schematic]) => !isValidOrCADLogicalSchematic(schematic) || selectedKeys.has(key)));
    return { ...def, schematics };
}
function collectLogicalSchematicEntries(def) {
    const entries = [];
    for (const [, schematic] of Object.entries(def.schematics)) {
        if (!isValidOrCADLogicalSchematic(schematic)) {
            continue;
        }
        entries.push({
            sourceFile: schematic.originalFileName,
            schematicViewNames: schematic.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema].Schematic.map((entry) => entry.Name),
            type: schematic.validationResult.data.fileFormat.type,
            design: schematic.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema],
        });
    }
    return entries;
}
function findLogicalSchematicEntry(entries, relativeToFile, referencedFile, referencedViewName) {
    const resolution = (0, orcadLogicalSchematicReferenceResolver_1.resolveOrCadLogicalSchematicReference)(entries, relativeToFile, referencedFile, referencedViewName);
    return resolution.status === 'resolved' ? resolution.candidate : undefined;
}
function findOccurrenceByRefInstId(occurrences, refInstId) {
    for (const occurrence of occurrences ?? []) {
        if (occurrence.RefInstId === refInstId) {
            return occurrence;
        }
        const child = findOccurrenceByRefInstId(occurrence.InstOccurrence, refInstId);
        if (child) {
            return child;
        }
    }
    return undefined;
}
function cloneOccurrence(occurrence) {
    return {
        ...occurrence,
        UserProperty: occurrence.UserProperty?.map((property) => ({ ...property })),
        DisplayProperty: occurrence.DisplayProperty?.map((property) => ({ ...property })),
        PortOccurrence: occurrence.PortOccurrence?.map((port) => ({
            ...port,
            UserProperty: port.UserProperty?.map((property) => ({ ...property })),
            DisplayProperty: port.DisplayProperty?.map((property) => ({ ...property })),
        })),
        TitleBlockOccurrence: occurrence.TitleBlockOccurrence?.map((titleBlock) => ({
            ...titleBlock,
            UserProperty: titleBlock.UserProperty?.map((property) => ({ ...property })),
            DisplayProperty: titleBlock.DisplayProperty?.map((property) => ({ ...property })),
        })),
        InstOccurrence: occurrence.InstOccurrence?.map(cloneOccurrence),
    };
}
function mergeOccurrenceUserProperties(base, override) {
    if (!override?.length) {
        return base?.map((property) => ({ ...property }));
    }
    const merged = new Map();
    for (const property of base ?? []) {
        merged.set(property.Name, { ...property });
    }
    for (const property of override) {
        merged.set(property.Name, { ...property });
    }
    return Array.from(merged.values());
}
function mergeOccurrenceWithOverride(base, override) {
    if (!override) {
        return cloneOccurrence(base);
    }
    const merged = cloneOccurrence(base);
    merged.UserProperty = mergeOccurrenceUserProperties(merged.UserProperty, override.UserProperty);
    merged.DisplayProperty = override.DisplayProperty?.map((property) => ({ ...property })) ?? merged.DisplayProperty;
    return merged;
}
function linkedTargetRootChildren(sourceOccurrence, targetDesign, targetViewName) {
    const targetChildren = occurrenceChildrenForSchematic(targetDesign, targetViewName);
    if (!targetChildren.length) {
        return sourceOccurrence.InstOccurrence ?? [];
    }
    return targetChildren.map((targetChild) => mergeOccurrenceWithOverride(targetChild, findOccurrenceByRefInstId(sourceOccurrence.InstOccurrence, targetChild.RefInstId)));
}
function occurrenceChildrenForSchematic(targetDesign, targetViewName) {
    const targetSchematic = targetDesign.Schematic.find((schematic) => schematic.Name.toLowerCase() === targetViewName.toLowerCase());
    const targetIds = new Set(targetSchematic?.Page.flatMap((page) => [
        ...(page.DrawnInst ?? []).map((instance) => String(instance.DbId)),
        ...(page.PartInst ?? []).map((instance) => String(instance.DbId)),
    ]) ?? []);
    const candidates = [];
    const visit = (occurrence) => {
        const children = occurrence.InstOccurrence ?? [];
        const score = children.filter((child) => targetIds.has(child.RefInstId)).length;
        if (score > 0) {
            candidates.push({ children, score });
        }
        for (const child of children) {
            visit(child);
        }
    };
    for (const occurrence of targetDesign.Occurrences.InstOccurrence) {
        visit(occurrence);
    }
    const bestScore = Math.max(0, ...candidates.map((candidate) => candidate.score));
    const best = candidates.filter((candidate) => candidate.score === bestScore);
    if (best.length === 1) {
        return best[0].children;
    }
    return (targetDesign.Occurrences.InstOccurrence.find((occurrence) => occurrence.RefInstId === '-1')?.InstOccurrence ?? []);
}
function collectOnlPlacementCatalog(def) {
    const placements = new Map();
    const fileGroups = [
        def.schematics,
        def.boards,
        def.netlists,
        def.boms,
        def.other,
    ];
    for (const group of fileGroups) {
        for (const file of Object.values(group ?? {})) {
            const netlist = file.validationResult?.data?.metadata?.[orcadValidation_1.OrCADXProjectMetadataKey.ONLNetlist];
            for (const component of netlist?.placement?.components ?? []) {
                for (const place of component.places ?? []) {
                    const key = normalizeOnlReference(place.refdes);
                    if (key && !placements.has(key)) {
                        placements.set(key, place);
                    }
                }
            }
        }
    }
    return placements;
}
function normalizeOnlReference(reference) {
    const normalized = reference?.trim().toUpperCase();
    return normalized && !normalized.endsWith('?') ? normalized : undefined;
}
function onlPropertyValue(value) {
    if (value === true) {
        return 'TRUE';
    }
    if (value === false) {
        return 'FALSE';
    }
    return String(value);
}
function userPropertiesFromOnlPlacement(place) {
    const properties = [];
    for (const [name, value] of Object.entries(place.properties ?? {})) {
        if (OnlPlacementOmittedPropertyKeys.has(onlPropertyKey(name))) {
            continue;
        }
        properties.push({ Name: name, Value: onlPropertyValue(value) });
    }
    return properties;
}
function hasDsnLogicalSchema(def) {
    return collectLogicalSchematicEntries(def).some((entry) => entry.type === orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic);
}
function hasAuthoritativeDsnCatalogMetadata(module) {
    const metadata = module.metadata ?? {};
    return typeof metadata.PN === 'string' && typeof metadata.DESCRIPTION === 'string';
}
function applyOnlPlacementPropertiesToModule(module, place) {
    if (hasAuthoritativeDsnCatalogMetadata(module)) {
        return;
    }
    const metadata = new Map(Object.entries(module.metadata ?? {}));
    const value = place.properties.Value;
    if (typeof value === 'string' || typeof value === 'number') {
        module.value = String(value);
        metadata.set('Value', String(value));
    }
    for (const property of userPropertiesFromOnlPlacement(place)) {
        metadata.set(property.Name, property.Value);
    }
    for (const name of Array.from(metadata.keys())) {
        if (OnlPlacementOnlyPropertyKeys.has(onlPropertyKey(name))) {
            metadata.delete(name);
        }
    }
    module.metadata = Object.fromEntries(metadata);
}
function applyOnlPlacementPropertiesToDsnProjectModel(project, def) {
    if (!hasDsnLogicalSchema(def)) {
        return;
    }
    const placements = collectOnlPlacementCatalog(def);
    if (placements.size === 0) {
        return;
    }
    for (const module of Object.values(project.modules)) {
        const reference = normalizeOnlReference(module.name);
        const placement = reference ? placements.get(reference) : undefined;
        if (placement) {
            applyOnlPlacementPropertiesToModule(module, placement);
        }
    }
}
function hierarchicalBlockPlacements(page) {
    const placements = [];
    for (const instance of page.DrawnInst ?? []) {
        placements.push({
            contentsLibName: instance.ContentsLibName,
            contentsViewName: instance.ContentsViewName,
            dbId: instance.DbId,
        });
    }
    for (const instance of page.PartInst ?? []) {
        if ((0, OrcadDsnUtils_1.isHierarchicalBlockPlacement)(instance)) {
            placements.push({
                contentsLibName: instance.ContentsLibName,
                contentsViewName: instance.ContentsViewName,
                dbId: instance.DbId,
            });
        }
    }
    return placements;
}
function normalizedIrefNetName(name) {
    const normalized = name?.trim().toUpperCase();
    return normalized ? normalized : undefined;
}
function strictPositiveInteger(value) {
    if (value === undefined || !/^\+?\d+$/.test(value.trim())) {
        return undefined;
    }
    const parsed = Number.parseInt(value, 10);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}
const MaxReportedIrefMismatches = 20;
const TitleBlockPageNumberPropertyName = 'Page Number';
function storedTitleBlockPageNumber(page) {
    const numbers = new Set();
    for (const titleBlock of page.TitleBlock ?? []) {
        for (const property of titleBlock.UserProperty ?? []) {
            if (property.Name !== TitleBlockPageNumberPropertyName) {
                continue;
            }
            const parsed = strictPositiveInteger(property.Value);
            if (parsed === undefined) {
                return undefined;
            }
            numbers.add(parsed);
        }
    }
    const [only] = numbers;
    return numbers.size === 1 && only !== undefined ? only : undefined;
}
const TitleBlockSchematicPathPropertyName = 'Schematic Path';
function storedOccurrencePageNumber(occurrence, page) {
    const numbers = new Set();
    for (const titleBlock of occurrence?.TitleBlockOccurrence ?? []) {
        const properties = titleBlock.UserProperty ?? [];
        const schematicPath = properties.find((property) => property.Name === TitleBlockSchematicPathPropertyName)?.Value;
        if (schematicPath === undefined || schematicPath.split('/').pop() !== page.Name) {
            continue;
        }
        for (const property of properties) {
            if (property.Name !== TitleBlockPageNumberPropertyName) {
                continue;
            }
            const parsed = strictPositiveInteger(property.Value);
            if (parsed === undefined) {
                return undefined;
            }
            numbers.add(parsed);
        }
    }
    const [only] = numbers;
    return numbers.size === 1 && only !== undefined ? only : undefined;
}
function storedDesignPageNumbers(design) {
    const numbers = new Map();
    const seen = new Set();
    for (const schematic of design.Schematic) {
        for (const page of schematic.Page) {
            const number = storedTitleBlockPageNumber(page);
            if (number === undefined || seen.has(number)) {
                return undefined;
            }
            seen.add(number);
            numbers.set(page, number);
        }
    }
    return numbers.size > 0 ? numbers : undefined;
}
function designReferenceRanks(entries, root) {
    const ranks = new Map([[root.sourceFile, 0]]);
    const queue = [root];
    while (queue.length > 0) {
        const entry = queue.shift();
        if (!entry) {
            break;
        }
        for (const schematic of entry.design.Schematic) {
            for (const page of schematic.Page) {
                for (const placement of hierarchicalBlockPlacements(page)) {
                    if (!placement.contentsLibName) {
                        continue;
                    }
                    const target = findLogicalSchematicEntry(entries, entry.sourceFile, placement.contentsLibName, placement.contentsViewName);
                    if (!target || ranks.has(target.sourceFile)) {
                        continue;
                    }
                    ranks.set(target.sourceFile, ranks.size);
                    queue.push(target);
                }
            }
        }
    }
    return ranks;
}
function rootLogicalSchematicEntry(entries, requestedSourceFile) {
    if (requestedSourceFile !== undefined) {
        const requestedKey = (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(requestedSourceFile).toLowerCase();
        return entries.find((entry) => (0, orcadLogicalSchematicReferenceResolver_1.normalizeOrCadLogicalSchematicPath)(entry.sourceFile).toLowerCase() === requestedKey);
    }
    const referenced = new Set();
    for (const entry of entries) {
        for (const schematic of entry.design.Schematic) {
            for (const page of schematic.Page) {
                for (const placement of hierarchicalBlockPlacements(page)) {
                    if (!placement.contentsLibName) {
                        continue;
                    }
                    const target = findLogicalSchematicEntry(entries, entry.sourceFile, placement.contentsLibName, placement.contentsViewName);
                    if (target && target.sourceFile !== entry.sourceFile) {
                        referenced.add(target.sourceFile);
                    }
                }
            }
        }
    }
    const roots = entries.filter((entry) => !referenced.has(entry.sourceFile));
    return roots.length === 1 ? roots[0] : undefined;
}
function pageNetNames(page) {
    const names = new Set();
    for (const net of page.NetScalar ?? []) {
        const name = normalizedIrefNetName(net.Name);
        if (name) {
            names.add(name);
        }
    }
    for (const bus of page.NetBus ?? []) {
        const busName = normalizedIrefNetName(bus.Name);
        if (busName) {
            names.add(busName);
        }
        for (const member of bus.NetScalar ?? []) {
            const memberName = normalizedIrefNetName(member.Name);
            if (memberName) {
                names.add(memberName);
            }
        }
    }
    return names;
}
function designReferencesMissingFile(entries, entry) {
    for (const schematic of entry.design.Schematic) {
        for (const page of schematic.Page) {
            for (const placement of hierarchicalBlockPlacements(page)) {
                if (placement.contentsLibName &&
                    !findLogicalSchematicEntry(entries, entry.sourceFile, placement.contentsLibName, placement.contentsViewName)) {
                    return true;
                }
            }
        }
    }
    return false;
}
function buildIntersheetPages(entries, pageBindings, requestedSourceFile, unnumberedDesigns) {
    const root = rootLogicalSchematicEntry(entries, requestedSourceFile);
    if (!root) {
        return undefined;
    }
    const ranks = designReferenceRanks(entries, root);
    const reached = entries.filter((entry) => ranks.has(entry.sourceFile));
    for (const entry of reached) {
        if (!storedDesignPageNumbers(entry.design)) {
            unnumberedDesigns?.push(entry.sourceFile);
        }
    }
    if (unnumberedDesigns?.length) {
        return undefined;
    }
    if (reached.some((entry) => designReferencesMissingFile(entries, entry))) {
        return undefined;
    }
    const entryByPage = new Map();
    for (const entry of entries) {
        for (const schematic of entry.design.Schematic) {
            for (const page of schematic.Page) {
                entryByPage.set(page, { entry, schematicName: schematic.Name });
            }
        }
    }
    const pages = [];
    for (const binding of pageBindings) {
        const owner = entryByPage.get(binding.page);
        const rank = owner ? ranks.get(owner.entry.sourceFile) : undefined;
        if (!owner || rank === undefined) {
            continue;
        }
        const localNumbers = storedDesignPageNumbers(owner.entry.design);
        if (!localNumbers) {
            return undefined;
        }
        const localNumber = storedOccurrencePageNumber(binding.occurrence, binding.page) ?? localNumbers.get(binding.page);
        if (localNumber === undefined) {
            return undefined;
        }
        pages.push({
            sourceFile: owner.entry.sourceFile,
            schematicName: owner.schematicName,
            page: binding.page,
            designId: binding.designId,
            occurrencePath: binding.occurrencePath,
            ...(binding.parentOccurrencePath === undefined ? {} : { parentOccurrencePath: binding.parentOccurrencePath }),
            ...(binding.sheetSymbolDbId === undefined ? {} : { sheetSymbolDbId: binding.sheetSymbolDbId }),
            localNumber,
            designRank: rank,
            projectNumber: 0,
            hasPartInstance: (binding.page.PartInst ?? []).length > 0,
            netNames: pageNetNames(binding.page),
            flatNets: { atComponentPins: binding.flatNetIdsAtComponentPins, all: binding.flatNetIds },
        });
    }
    pages.sort((left, right) => left.localNumber - right.localNumber ||
        left.designRank - right.designRank ||
        left.designId.localeCompare(right.designId));
    pages.forEach((page, index) => {
        page.projectNumber = index + 1;
    });
    return pages;
}
function placementParentPageNumbers(placements) {
    const byOccurrencePath = new Map();
    for (const placement of placements) {
        const siblings = byOccurrencePath.get(placement.occurrencePath) ?? [];
        siblings.push(placement);
        byOccurrencePath.set(placement.occurrencePath, siblings);
    }
    const parents = new Map();
    for (const placement of placements) {
        const { parentOccurrencePath, sheetSymbolDbId } = placement;
        if (parentOccurrencePath === undefined || sheetSymbolDbId === undefined) {
            continue;
        }
        const hosts = (byOccurrencePath.get(parentOccurrencePath) ?? []).filter((candidate) => hierarchicalBlockPlacements(candidate.page).some((block) => block.dbId === sheetSymbolDbId));
        const [only] = hosts;
        if (hosts.length === 1 && only !== undefined) {
            parents.set(placement.designId, only.projectNumber);
        }
    }
    return parents;
}
function isUnverifiedIntersheetReference(host, name) {
    return !host.hasPartInstance && !host.netNames.has(name);
}
function formatIrefValue(pageNumbers, unverified, parentPageNumber) {
    const tokens = new Map();
    for (const pageNumber of pageNumbers) {
        if (pageNumber !== parentPageNumber) {
            tokens.set(pageNumber, `${pageNumber}${unverified ? '!' : ''}`);
        }
    }
    if (parentPageNumber !== undefined) {
        tokens.set(parentPageNumber, `${parentPageNumber}^`);
    }
    if (tokens.size === 0) {
        return undefined;
    }
    return Array.from(tokens.keys())
        .sort((left, right) => left - right)
        .map((pageNumber) => tokens.get(pageNumber) ?? '')
        .join(',');
}
function storedIrefValue(userProperties) {
    const stored = userProperties?.find((property) => property.Name === IntersheetReferencePropertyName)?.Value;
    return stored !== undefined && stored.trim().length > 0 ? stored : undefined;
}
function pageCarriesOnlyGlobalSymbols(page) {
    return !page.hasPartInstance && (page.page.Global ?? []).length > 0;
}
function flatNetsByObject(bindings, objectDbId) {
    const byDesign = new Map();
    for (const binding of bindings) {
        const byDbId = byDesign.get(binding.designId) ?? new Map();
        const flatNetIds = byDbId.get(objectDbId(binding)) ?? new Set();
        for (const flatNetId of binding.flatNetIds) {
            flatNetIds.add(flatNetId);
        }
        byDbId.set(objectDbId(binding), flatNetIds);
        byDesign.set(binding.designId, byDbId);
    }
    return byDesign;
}
function componentPinPageNumbers(flatNetIds, pages) {
    const numbers = [];
    for (const page of pages) {
        for (const flatNetId of flatNetIds) {
            if (page.flatNets.atComponentPins.has(flatNetId)) {
                numbers.push(page.projectNumber);
                break;
            }
        }
    }
    return numbers;
}
function flatNetPageNumbers(flatNetIds, pages) {
    const numbers = [];
    for (const page of pages) {
        for (const flatNetId of flatNetIds) {
            if (page.flatNets.all.has(flatNetId)) {
                numbers.push(page.projectNumber);
                break;
            }
        }
    }
    return numbers;
}
function hostPageRepeatsConnector(host, connectorDbId, flatNetIds, connectorFlatNets) {
    const byDbId = connectorFlatNets.get(host.designId);
    if (!byDbId) {
        return false;
    }
    for (const [otherDbId, otherFlatNetIds] of byDbId) {
        if (otherDbId === connectorDbId) {
            continue;
        }
        for (const flatNetId of otherFlatNetIds) {
            if (flatNetIds.has(flatNetId)) {
                return true;
            }
        }
    }
    return false;
}
const GridIrefValuePattern = /^\[Grid.+\]$/;
const PageNumberIrefValuePattern = /^\d+[!^]?(,\d+[!^]?)*$/;
var IntersheetReferenceForm;
(function (IntersheetReferenceForm) {
    IntersheetReferenceForm["PageNumber"] = "page_number";
    IntersheetReferenceForm["Grid"] = "grid";
    IntersheetReferenceForm["Ambiguous"] = "ambiguous";
})(IntersheetReferenceForm || (IntersheetReferenceForm = {}));
function designIntersheetReferenceForm(design) {
    const stored = design.IntersheetReferenceValues;
    if (stored === undefined) {
        return IntersheetReferenceForm.PageNumber;
    }
    const values = stored.filter((value) => value.trim().length > 0);
    if (values.length > 0 && values.every((value) => GridIrefValuePattern.test(value))) {
        return IntersheetReferenceForm.Grid;
    }
    if (values.length > 0 && values.every((value) => PageNumberIrefValuePattern.test(value))) {
        return IntersheetReferenceForm.PageNumber;
    }
    return IntersheetReferenceForm.Ambiguous;
}
function storedIntersheetReferences(entries, placements) {
    const sourceFileByPage = new Map();
    for (const entry of entries) {
        for (const schematic of entry.design.Schematic) {
            for (const page of schematic.Page) {
                sourceFileByPage.set(page, entry.sourceFile);
            }
        }
    }
    const placementsPerPage = new Map();
    for (const placement of placements) {
        placementsPerPage.set(placement.page, (placementsPerPage.get(placement.page) ?? 0) + 1);
    }
    const references = new Map();
    let reusedPageConnectors = 0;
    for (const placement of placements) {
        const sourceFile = sourceFileByPage.get(placement.page);
        if (sourceFile === undefined) {
            continue;
        }
        if ((placementsPerPage.get(placement.page) ?? 0) > 1) {
            reusedPageConnectors +=
                (placement.page.Port ?? []).filter((port) => storedIrefValue(port.PortUserProp) !== undefined).length +
                    (placement.page.OffPageConnector ?? []).filter((connector) => storedIrefValue(connector.OffPageConnectorUserProp) !== undefined).length;
            continue;
        }
        const claim = (objectKind, objectDbId, value) => {
            if (value === undefined) {
                return;
            }
            references.set(intersheetReferenceLabelKey(placement.designId, objectKind, objectDbId), {
                sourceFile,
                designId: placement.designId,
                objectKind,
                objectDbId,
                value,
                source: orcadIntersheetReferences_1.OrCadIntersheetReferenceSource.Stored,
            });
        };
        for (const port of placement.page.Port ?? []) {
            claim(orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port, port.DbId, storedIrefValue(port.PortUserProp));
        }
        for (const connector of placement.page.OffPageConnector ?? []) {
            claim(orcadIntersheetReferences_1.OrCadIntersheetObjectKind.OffPageConnector, connector.DbId, storedIrefValue(connector.OffPageConnectorUserProp));
        }
    }
    return { references, reusedPageConnectors };
}
function computeProjectIntersheetReferences(def, transformResult, requestedSourceFile) {
    const entries = collectLogicalSchematicEntries(def);
    const { references: stored, reusedPageConnectors } = storedIntersheetReferences(entries, transformResult.pageNetBindings);
    const uncorroborated = (reason) => ({
        references: Array.from(stored.values()),
        results: stored.size === 0 ?
            []
            : [
                {
                    type: OrCadStoredIntersheetReferenceResultType,
                    message: `The design file's own intersheet reference was emitted unchecked on ${stored.size} connector(s) because ${reason}.`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                },
            ],
    });
    const noReferences = {
        ...uncorroborated('the project has no compiled pages to check it against'),
        pagesByDesignId: new Map(),
    };
    if (entries.length === 0) {
        return noReferences;
    }
    let unvaluedCount = 0;
    for (const entry of entries) {
        for (const schematic of entry.design.Schematic) {
            for (const page of schematic.Page) {
                for (const port of page.Port ?? []) {
                    unvaluedCount += storedIrefValue(port.PortUserProp) === undefined ? 1 : 0;
                }
                for (const connector of page.OffPageConnector ?? []) {
                    unvaluedCount += storedIrefValue(connector.OffPageConnectorUserProp) === undefined ? 1 : 0;
                }
            }
        }
    }
    const unnumberedDesigns = [];
    const pages = buildIntersheetPages(entries, transformResult.pageNetBindings, requestedSourceFile, unnumberedDesigns);
    if (!pages) {
        const reason = unnumberedDesigns.length > 0 ?
            `these designs carry no complete stored title-block page numbering: ${unnumberedDesigns.join(', ')}`
            : 'the requested root design is not part of this project, or the project does not carry a design a block on it references';
        const unchecked = uncorroborated('the project page numbering could not be resolved');
        return {
            references: unchecked.references,
            pagesByDesignId: new Map(),
            results: [
                ...unchecked.results,
                {
                    type: OrCadIntersheetReferenceResultType,
                    message: `Intersheet references were left empty on ${unvaluedCount} connector(s): the project page numbering could not be resolved because ${reason}.`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                },
            ],
        };
    }
    const numberedPages = new Set(pages.map((page) => page.page));
    const uncompiledPages = new Set(transformResult.uncompiledNetObjects.filter((object) => object.portDbId === undefined).map((object) => object.page));
    const uncompiledPorts = new Map();
    for (const object of transformResult.uncompiledNetObjects) {
        if (object.portDbId === undefined) {
            continue;
        }
        const portDbIds = uncompiledPorts.get(object.page) ?? new Set();
        portDbIds.add(object.portDbId);
        uncompiledPorts.set(object.page, portDbIds);
    }
    const parentPageNumbers = placementParentPageNumbers(pages);
    const portFlatNets = flatNetsByObject(transformResult.portNetBindings, (binding) => binding.portDbId);
    const connectorFlatNets = flatNetsByObject(transformResult.offPageConnectorNetBindings, (binding) => binding.connectorDbId);
    const tally = {
        unbound: 0,
        parentless: 0,
        unreferenced: 0,
        unnamed: 0,
        gridForm: 0,
        ambiguousForm: 0,
        outsideProject: 0,
        globalOnlyPage: 0,
        uncompiled: 0,
        unresolvedFlatNet: 0,
    };
    for (const entry of entries) {
        for (const schematic of entry.design.Schematic) {
            for (const page of schematic.Page) {
                if (numberedPages.has(page)) {
                    continue;
                }
                for (const connector of page.OffPageConnector ?? []) {
                    tally.outsideProject += storedIrefValue(connector.OffPageConnectorUserProp) === undefined ? 1 : 0;
                }
                for (const port of page.Port ?? []) {
                    tally.outsideProject += storedIrefValue(port.PortUserProp) === undefined ? 1 : 0;
                }
            }
        }
    }
    const referenceFormBySourceFile = new Map(entries.map((entry) => [entry.sourceFile, designIntersheetReferenceForm(entry.design)]));
    const unresolvedFlatNets = new Map(transformResult.unresolvedFlatNets.map((net) => [net.flatNetId, net.reason]));
    const unresolvedReasons = new Set();
    const mismatches = [];
    const unresolvedReason = (flatNetIds) => {
        for (const flatNetId of flatNetIds) {
            const reason = unresolvedFlatNets.get(flatNetId);
            if (reason !== undefined) {
                return reason;
            }
        }
        return undefined;
    };
    const references = [];
    const claimed = new Set();
    let uncorroboratedCount = 0;
    const settle = (host, objectKind, objectDbId, computed) => {
        const key = intersheetReferenceLabelKey(host.designId, objectKind, objectDbId);
        claimed.add(key);
        const storedReference = stored.get(key);
        if (storedReference === undefined) {
            if (computed !== undefined) {
                references.push({
                    sourceFile: host.sourceFile,
                    designId: host.designId,
                    objectKind,
                    objectDbId,
                    value: computed,
                    source: orcadIntersheetReferences_1.OrCadIntersheetReferenceSource.Computed,
                });
            }
            return;
        }
        if (computed === undefined) {
            uncorroboratedCount++;
            references.push(storedReference);
            return;
        }
        references.push(storedReference);
        if (computed !== storedReference.value) {
            mismatches.push(`${objectKind === orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port ? 'port' : 'off-page connector'} ${objectDbId} of ${host.sourceFile} stores "${storedReference.value}" where the project computes "${computed}"`);
        }
    };
    for (const host of pages) {
        const parentPageNumber = parentPageNumbers.get(host.designId);
        const pageIsUncompiled = uncompiledPages.has(host.page);
        const referenceForm = referenceFormBySourceFile.get(host.sourceFile) ?? IntersheetReferenceForm.PageNumber;
        const pageUsesGridForm = referenceForm === IntersheetReferenceForm.Grid;
        const pageFormIsAmbiguous = referenceForm === IntersheetReferenceForm.Ambiguous;
        const pageCorroborationUnsettled = pageCarriesOnlyGlobalSymbols(host);
        for (const connector of host.page.OffPageConnector ?? []) {
            const kind = orcadIntersheetReferences_1.OrCadIntersheetObjectKind.OffPageConnector;
            const isStored = stored.has(intersheetReferenceLabelKey(host.designId, kind, connector.DbId));
            const refuse = (count) => {
                if (!isStored) {
                    count();
                }
                settle(host, kind, connector.DbId, undefined);
            };
            if (pageUsesGridForm) {
                refuse(() => tally.gridForm++);
                continue;
            }
            if (pageFormIsAmbiguous) {
                refuse(() => tally.ambiguousForm++);
                continue;
            }
            const name = normalizedIrefNetName(connector.Name);
            if (!name) {
                refuse(() => tally.unnamed++);
                continue;
            }
            if (pageIsUncompiled) {
                refuse(() => tally.uncompiled++);
                continue;
            }
            const flatNetIds = connectorFlatNets.get(host.designId)?.get(connector.DbId);
            if (!flatNetIds || flatNetIds.size === 0) {
                refuse(() => tally.unbound++);
                continue;
            }
            const unresolved = unresolvedReason(flatNetIds);
            if (unresolved !== undefined) {
                unresolvedReasons.add(unresolved);
                refuse(() => tally.unresolvedFlatNet++);
                continue;
            }
            const unverified = isUnverifiedIntersheetReference(host, name);
            if (unverified && pageCorroborationUnsettled) {
                refuse(() => tally.globalOnlyPage++);
                continue;
            }
            const referenced = flatNetPageNumbers(flatNetIds, pages).filter((pageNumber) => pageNumber !== host.projectNumber ||
                hostPageRepeatsConnector(host, connector.DbId, flatNetIds, connectorFlatNets));
            const value = formatIrefValue(referenced, unverified);
            if (value === undefined && !isStored) {
                tally.unreferenced++;
            }
            settle(host, kind, connector.DbId, value);
        }
        for (const port of host.page.Port ?? []) {
            const kind = orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port;
            const isStored = stored.has(intersheetReferenceLabelKey(host.designId, kind, port.DbId));
            const refuse = (count) => {
                if (!isStored) {
                    count();
                }
                settle(host, kind, port.DbId, undefined);
            };
            if (pageUsesGridForm) {
                refuse(() => tally.gridForm++);
                continue;
            }
            if (pageFormIsAmbiguous) {
                refuse(() => tally.ambiguousForm++);
                continue;
            }
            if (pageIsUncompiled || uncompiledPorts.get(host.page)?.has(port.DbId) === true) {
                refuse(() => tally.uncompiled++);
                continue;
            }
            const flatNetIds = portFlatNets.get(host.designId)?.get(port.DbId);
            if (!flatNetIds || flatNetIds.size === 0) {
                refuse(() => tally.unbound++);
                continue;
            }
            const unresolved = unresolvedReason(flatNetIds);
            if (unresolved !== undefined) {
                unresolvedReasons.add(unresolved);
                refuse(() => tally.unresolvedFlatNet++);
                continue;
            }
            if (parentPageNumber === undefined) {
                refuse(() => tally.parentless++);
                continue;
            }
            const name = normalizedIrefNetName(port.Name);
            const unverified = name !== undefined && isUnverifiedIntersheetReference(host, name);
            if (unverified && pageCorroborationUnsettled) {
                refuse(() => tally.globalOnlyPage++);
                continue;
            }
            const value = formatIrefValue(componentPinPageNumbers(flatNetIds, pages).filter((pageNumber) => pageNumber !== host.projectNumber), unverified, parentPageNumber);
            if (value === undefined && !isStored) {
                tally.unreferenced++;
            }
            settle(host, kind, port.DbId, value);
        }
    }
    for (const [key, reference] of stored) {
        if (!claimed.has(key)) {
            uncorroboratedCount++;
            references.push(reference);
        }
    }
    const results = intersheetReferenceResults(tally, unresolvedReasons);
    if (reusedPageConnectors > 0) {
        results.push({
            type: OrCadStoredIntersheetReferenceResultType,
            message: `The stored intersheet reference was passed over on ${reusedPageConnectors} connector(s) of a page the project places more than once, because the value belongs to one placement and only the page's own record is decoded.`,
            severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
        });
    }
    if (uncorroboratedCount > 0) {
        results.push({
            type: OrCadStoredIntersheetReferenceResultType,
            message: `The design file's own intersheet reference was emitted unchecked on ${uncorroboratedCount} connector(s): the project's pages and nets settle no value of their own for them.`,
            severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
        });
    }
    if (mismatches.length > 0) {
        results.push({
            type: OrCadIntersheetReferenceMismatchResultType,
            message: `The design file's own intersheet reference was emitted on ${mismatches.length} connector(s) the project computes differently, because the file's value is the decoded one: ${mismatches.sort().slice(0, MaxReportedIrefMismatches).join('; ')}.`,
            severity: sigma_engine_shared_1.BoundaryLevel.Error,
        });
    }
    return {
        references,
        results,
        pagesByDesignId: new Map(pages.map((page) => [page.designId, page.page])),
    };
}
function intersheetReferenceLabelKey(designId, kind, dbId) {
    return JSON.stringify([designId, kind, dbId]);
}
function intersheetReferenceLabels(references, pagesByDesignId) {
    const valuesByObject = new Map(references.map((reference) => [
        intersheetReferenceLabelKey(reference.designId, reference.objectKind, reference.objectDbId),
        reference.value,
    ]));
    const labels = new Map();
    const claim = (designId, kind, dbId, displayProperties) => {
        const key = intersheetReferenceLabelKey(designId, kind, dbId);
        const value = valuesByObject.get(key);
        const displayProperty = displayProperties?.find((property) => property.Name === IntersheetReferencePropertyName);
        if (value !== undefined && displayProperty) {
            labels.set(key, { displayProperty, value });
        }
    };
    for (const reference of references) {
        const page = pagesByDesignId.get(reference.designId);
        if (!page || reference.source !== orcadIntersheetReferences_1.OrCadIntersheetReferenceSource.Computed) {
            continue;
        }
        if (reference.objectKind === orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port) {
            const port = (page.Port ?? []).find((candidate) => candidate.DbId === reference.objectDbId);
            claim(reference.designId, reference.objectKind, reference.objectDbId, port?.PortDisplayProp);
            continue;
        }
        const connector = (page.OffPageConnector ?? []).find((candidate) => candidate.DbId === reference.objectDbId);
        claim(reference.designId, reference.objectKind, reference.objectDbId, connector?.OffPageConnectorDisplayProp);
    }
    return labels;
}
function drawIntersheetReferenceLabels(project, labels) {
    if (labels.size === 0) {
        return;
    }
    for (const design of Object.values(project.designs)) {
        if (design.modelType !== 'schematic') {
            continue;
        }
        for (const layerDrawings of Object.values(design.drawings)) {
            for (const drawing of layerDrawings) {
                if (drawing.type !== 'group') {
                    continue;
                }
                const kind = drawing.metadata?.[OrcadDsnSymbolUtils_1.OrCadCrossReferenceKindMetadataKey];
                const dbId = drawing.metadata?.[OrcadDsnSymbolUtils_1.OrCadCrossReferenceDbIdMetadataKey];
                if (typeof kind !== 'string' || typeof dbId !== 'number') {
                    continue;
                }
                const label = labels.get(intersheetReferenceLabelKey(design.designId, kind, dbId));
                if (!label) {
                    continue;
                }
                const fontSize = (0, OrcadDsnSymbolUtils_1.scaleFont)(label.displayProperty.Font);
                drawing.drawings.unshift({
                    type: 'text',
                    value: label.value,
                    pos: [label.displayProperty.LocX, label.displayProperty.LocY + fontSize],
                    angle: label.displayProperty.Rotation * 90,
                    justify: [-1, -1],
                    size: fontSize,
                    thickness: 1,
                });
            }
        }
    }
}
function intersheetReferenceResults(tally, unresolvedReasons) {
    const messages = [
        [tally.unbound, 'connector(s) the transform put on no compiled net, so the pages their net reaches are unknown'],
        [
            tally.parentless,
            'hierarchical port(s) whose schematic folder is reached from no single page, so the parent-page marker could not be placed',
        ],
        [tally.unreferenced, 'connector(s) whose net reaches no other page of the project'],
        [tally.unnamed, 'off-page connector(s) that carry no name'],
        [
            tally.gridForm,
            "connector(s) whose stored value addresses pages by drawing-frame grid zone, which the design's template defines and the design file does not carry",
        ],
        [
            tally.outsideProject,
            'connector(s) on pages the requested root design does not reach, so they are not part of the numbered project',
        ],
        [tally.uncompiled, 'connector(s) on a page or label the transform could not compile'],
        [
            tally.ambiguousForm,
            'connector(s) whose design references values of no one form, so the form its template writes cannot be read',
        ],
        [
            tally.globalOnlyPage,
            'connector(s) on a page that places no part but does place global power or ground symbols; whether Capture counts a global symbol as corroboration for the unverified marker is not settled by the current fixtures',
        ],
        [
            tally.unresolvedFlatNet,
            `connector(s) whose flat net the naming rules did not resolve: ${Array.from(unresolvedReasons).sort().join('; ')}`,
        ],
    ];
    const results = [];
    for (const [count, reason] of messages) {
        if (count > 0) {
            results.push({
                type: OrCadIntersheetReferenceResultType,
                message: `Intersheet references were left empty on ${count} ${reason}.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
            });
        }
    }
    return results;
}
function crossFileLinkTargets(entries) {
    const targetsByDesign = new Map();
    for (const entry of entries) {
        if (entry.type !== orcadValidation_1.OrCADXFileType.OrCADCaptureSchematic) {
            continue;
        }
        for (const schematic of entry.design.Schematic) {
            for (const page of schematic.Page) {
                for (const instance of [...(page.DrawnInst ?? []), ...(page.PartInst ?? [])]) {
                    if (!instance.ContentsLibName) {
                        continue;
                    }
                    const target = findLogicalSchematicEntry(entries, entry.sourceFile, instance.ContentsLibName, instance.ContentsViewName);
                    if (!target || target === entry) {
                        continue;
                    }
                    const targets = targetsByDesign.get(entry.design) ?? new Map();
                    targets.set(String(instance.DbId), {
                        design: target.design,
                        viewName: instance.ContentsViewName,
                        sourceFile: target.sourceFile,
                    });
                    targetsByDesign.set(entry.design, targets);
                }
            }
        }
    }
    return targetsByDesign;
}
const OrCadCrossFileLinkCycleResultType = 'cross-file-link-cycle';
function withLinkedOccurrences(occurrences, path, targetsByDesign, cycles) {
    const design = path[path.length - 1]?.design;
    if (!occurrences || design === undefined) {
        return occurrences;
    }
    const describe = (entry) => `${entry.sourceFile}/${entry.viewName}`;
    let changed = false;
    const rebuilt = occurrences.map((occurrence) => {
        const target = targetsByDesign.get(design)?.get(occurrence.RefInstId);
        if (target) {
            const targetEntry = { design: target.design, sourceFile: target.sourceFile, viewName: target.viewName };
            if (path.some((entry) => entry.sourceFile === target.sourceFile && entry.viewName === target.viewName)) {
                cycles.add([...path.map(describe), describe(targetEntry)].join(' -> '));
                return occurrence;
            }
            changed = true;
            const linked = linkedTargetRootChildren(occurrence, target.design, target.viewName);
            const targetPath = [...path, targetEntry];
            return {
                ...occurrence,
                InstOccurrence: (withLinkedOccurrences(linked, targetPath, targetsByDesign, cycles) ??
                    linked),
            };
        }
        const rebuiltChildren = withLinkedOccurrences(occurrence.InstOccurrence, path, targetsByDesign, cycles);
        if (rebuiltChildren === occurrence.InstOccurrence) {
            return occurrence;
        }
        changed = true;
        return { ...occurrence, InstOccurrence: rebuiltChildren };
    });
    return changed ? rebuilt : occurrences;
}
function linkDsnCrossFileOccurrences(def, results) {
    const entries = collectLogicalSchematicEntries(def);
    if (entries.length <= 1) {
        return def;
    }
    const sourceFileByDesign = new Map(entries.map((entry) => [entry.design, entry.sourceFile]));
    const cycles = new Set();
    const targetsByDesign = crossFileLinkTargets(entries);
    if (targetsByDesign.size === 0) {
        return def;
    }
    const schematics = Object.fromEntries(Object.entries(def.schematics).map(([key, schematic]) => {
        if (!isValidOrCADLogicalSchematic(schematic)) {
            return [key, schematic];
        }
        const metadata = schematic.validationResult.data.metadata;
        const design = metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema];
        const rebuilt = withLinkedOccurrences(design.Occurrences.InstOccurrence, [{ design, sourceFile: sourceFileByDesign.get(design) ?? key, viewName: design.RootName }], targetsByDesign, cycles);
        if (rebuilt === design.Occurrences.InstOccurrence) {
            return [key, schematic];
        }
        const linkedDesign = {
            ...design,
            Occurrences: { InstOccurrence: (rebuilt ?? []) },
        };
        return [
            key,
            {
                ...schematic,
                validationResult: {
                    ...schematic.validationResult,
                    data: {
                        ...schematic.validationResult.data,
                        metadata: { ...metadata, [orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema]: linkedDesign },
                    },
                },
            },
        ];
    }));
    if (results && cycles.size > 0) {
        results.push({
            type: OrCadCrossFileLinkCycleResultType,
            message: `Block placements reference one another in a loop, so the contents of the design each loop closes on are left where the loop begins: ${Array.from(cycles).sort().join('; ')}.`,
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
        });
    }
    return { ...def, schematics };
}
function unresolvedReferenceProcessingResult(reference) {
    return {
        type: 'orcad-referenced-project-file-unresolved',
        message: `Referenced OrCAD schematic could not be resolved: ${reference.referencedFile} (${reference.reason})`,
        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
        referencedFrom: reference.referencedFrom,
        reference: reference.referencedFile,
        normalizedPath: reference.normalizedPath,
        reason: reference.reason,
        candidates: reference.candidates,
    };
}
function collectCfbDecodeDiagnostics(def) {
    const results = [];
    for (const schematic of Object.values(def.schematics)) {
        if (!isValidOrCADLogicalSchematic(schematic)) {
            continue;
        }
        const diagnostics = schematic.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchemaDiagnostics];
        if (Array.isArray(diagnostics)) {
            for (const diagnostic of diagnostics) {
                results.push(diagnostic);
            }
        }
    }
    return results;
}
function viewResolvedReferenceProcessingResult(reference) {
    return {
        type: 'orcad-referenced-project-file-resolved-via-view',
        message: `Stale OrCAD schematic path resolved by view ${reference.viewName}: ${reference.referencedFile} -> ${reference.resolvedFile}`,
        severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
        referencedFrom: reference.referencedFrom,
        reference: reference.referencedFile,
        normalizedPath: reference.normalizedPath,
        resolvedFile: reference.resolvedFile,
        viewName: reference.viewName,
        matchKind: reference.matchKind,
    };
}
function parseProject(def, initialSettings = {}) {
    const settings = { ...getDefaultSettings(), ...initialSettings };
    if (def.type !== 'orcad') {
        throw new Error(`Attempted to parse non-OrCAD project def: ${def.type}`);
    }
    if (settings.legacyParser) {
        return { projectModel: parseProjectLegacy(def, settings), extractedImages: [], intersheetReferences: [] };
    }
    const crossFileLinkResults = [];
    const logicalDef = linkDsnCrossFileOccurrences(preferDsnLogicalSchemas(def), crossFileLinkResults);
    const referenceAudit = auditReferencedProjectFiles(logicalDef);
    const processingResults = referenceAudit.unresolvedReferences.map(unresolvedReferenceProcessingResult);
    processingResults.push(...crossFileLinkResults);
    processingResults.push(...referenceAudit.viewResolvedReferences.map(viewResolvedReferenceProcessingResult));
    for (const diagnostic of collectCfbDecodeDiagnostics(logicalDef)) {
        processingResults.push(diagnostic);
    }
    if (!referenceAudit.hasValidSchematics) {
        throw new Error('No valid OrCAD Capture schematic XML files found in project');
    }
    const combobulationDebugLayerId = settings.combobulationDebug ? orcadLayers_1.OrCadSchLayers.find((l) => l.type === sigma_engine_shared_1.SchLayerType.Other).id : undefined;
    const transformResult = (0, OrcadDsnOrchestratedTransformer_1.transformDsnProject)(logicalDef, {
        ...(combobulationDebugLayerId !== undefined ? { combobulationDebugLayerId } : {}),
        ...(settings.requestedSourceFile !== undefined ? { requestedSourceFile: settings.requestedSourceFile } : {}),
        netsMustHaveWireOrMultipleConnections: true,
        ...(initialSettings.transformOpts ?? {}),
    });
    if (transformResult.projectModels.length === 0) {
        throw new Error('Orchestrated transformer produced no project models');
    }
    if (transformResult.rootSourceFiles.length > 1) {
        processingResults.push({
            type: 'orcad-parsing',
            message: `Multiple independent OrCAD roots produced project models (${transformResult.rootSourceFiles.length}): ${transformResult.rootSourceFiles.join(', ')}. Deterministically selected ${transformResult.rootSourceFiles[0]}; request a specific DSN source file to select another root.`,
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
        });
    }
    const project = transformResult.projectModels[0];
    const intersheetReferences = computeProjectIntersheetReferences(logicalDef, transformResult, settings.requestedSourceFile);
    processingResults.push(...intersheetReferences.results);
    drawIntersheetReferenceLabels(project, intersheetReferenceLabels(intersheetReferences.references, intersheetReferences.pagesByDesignId));
    if (settings.mergeDisjointComponents) {
        (0, mergeDisjointComponentsUtil_1.mergeDisjointComponents)(project);
    }
    applyOnlPlacementPropertiesToDsnProjectModel(project, logicalDef);
    project.modelVersion = ModelText_1.MODEL_TEXT_MIN_VERSION;
    for (const result of processingResults) {
        project.processingResults.innerResults.push(result);
    }
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
    return {
        projectModel: project,
        extractedImages: transformResult.extractedImages,
        intersheetReferences: intersheetReferences.references,
    };
}
function parseProjectLegacy(def, settings) {
    const logicalDef = linkDsnCrossFileOccurrences(preferDsnLogicalSchemas(def));
    const referenceAudit = auditReferencedProjectFiles(logicalDef);
    if (!referenceAudit.hasValidSchematics) {
        throw new Error('No valid OrCAD Capture schematic XML files found in project');
    }
    const schematicMap = new Map();
    for (const [, xmlSch] of Object.entries(logicalDef.schematics)) {
        if (isValidOrCADLogicalSchematic(xmlSch)) {
            const parsedDesign = xmlSch.validationResult.data.metadata[orcadValidation_1.OrCADXProjectMetadataKey.LogicalSchema];
            if (!parsedDesign) {
                throw new Error(`No parsed design found for ${xmlSch.originalFileName}`);
            }
            schematicMap.set(xmlSch.originalFileName, parsedDesign);
        }
    }
    const transformer = new orcadDsnTransformer_1.OrCadDSNTransformer(Array.from(schematicMap.values()));
    const project = transformer.transformToProjectModel();
    if (settings.mergeDisjointComponents) {
        (0, mergeDisjointComponentsUtil_1.mergeDisjointComponents)(project);
    }
    applyOnlPlacementPropertiesToDsnProjectModel(project, logicalDef);
    project.processingResults.innerResults.push(...referenceAudit.unresolvedReferences.map(unresolvedReferenceProcessingResult), ...referenceAudit.viewResolvedReferences.map(viewResolvedReferenceProcessingResult));
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(project.processingResults);
    return project;
}
//# sourceMappingURL=parseOrCADProject.js.map